import React, { useState } from "react";
import { RiCloseLine } from "react-icons/ri";
import { toast } from "react-toastify";
import cities from "../POS/cities.json";
import countries from "../POS/countries.json";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createZone } from "UpdatedFeatures/api";

const AddZoneModal = ({ open, setOpen }) => {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [locations, setLocations] = useState([{ city: "", street: "" }]);

  const queryClient = useQueryClient();

  const CreateZoneMutation = useMutation({
    mutationFn: createZone,
    onSuccess: () => {
      toast.success("Entrepôt créé avec succès");
      queryClient.invalidateQueries(["zones"]);
      setOpen(false);
      setSelectedCountry("");
      setLocations([{ city: "", street: "" }]);
    },
    onError: (error) => {
      toast.error(`Erreur lors de la création de l'entrepôt: ${error.message}`);
    },
  });
  function convertUnicodeToEmoji(unicode) {
    return unicode
      .split(" ")
      .map((u) => String.fromCodePoint(parseInt(u.replace("U+", ""), 16)))
      .join("");
  }
  const handleAddLocation = () => {
    setLocations([...locations, { city: "", street: "" }]);
  };
  const handleClose = () => {
    setOpen(false);
    setSelectedCountry("");
    setLocations([{ city: "", street: "" }]);
  };

  const handleLocationChange = (index, field, value) => {
    const updated = [...locations];
    updated[index][field] = value;
    setLocations(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedCountry || locations.some((loc) => !loc.city || !loc.street)) {
      toast.error("Tous les champs sont requis");
      return;
    }

    const countryName =
      countries.find((c) => c.id === Number(selectedCountry))?.name_fr || "";

    CreateZoneMutation.mutate({
      country: countryName,
      locations: locations.map((loc) => ({
        city: loc.city,
        street_address: loc.street,
      })),
    });
  };

  const sortedCountries = [...countries].sort((a, b) =>
    a.name_fr.localeCompare(b.name_fr)
  );
  const countryStates = cities.filter(
    (city) => city.stateId === String(selectedCountry)
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" style={{ zIndex: 9990 }}>
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75"
        onClick={() => setOpen(false)}
      />

      <div className="flex items-center justify-center min-h-screen p-4 overflow-y-auto">
        <div
          className="relative w-full max-w-md mx-auto bg-white shadow-lg rounded-lg overflow-hidden max-h-[calc(100vh-4rem)] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center p-6 bg-[#b269ae] border-b border-gray-500/30 pb-4">
            <h3 className="text-lg font-medium leading-6 text-white">
              Ajouter une nouvelle zone
            </h3>
            <button
              type="button"
              className="inline-flex items-center justify-center p-1.5 hover:scale-105 duration-200"
              onClick={() => setOpen(false)}
            >
              <RiCloseLine className="h-6 w-6 text-white" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 p-6">
            <div className="grid grid-cols-1 gap-y-4 mt-4">
              {/* Country */}
              <div>
                <label
                  htmlFor="country"
                  className="block text-sm font-medium text-gray-900"
                >
                  Pays
                </label>
                <select
                  id="country"
                  value={selectedCountry}
                  onChange={(e) => {
                    setSelectedCountry(e.target.value);
                    setLocations([{ city: "", street: "" }]);
                  }}
                  className="mt-2 block w-full rounded-lg border border-gray-500/30 px-4 py-3 text-gray-900 shadow-sm focus:ring-2 focus:ring-[#b269ae] sm:text-sm"
                >
                  <option value="">Sélectionner un pays</option>
                  {sortedCountries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {convertUnicodeToEmoji(country.emojiU)} {country.name_fr}
                    </option>
                  ))}
                </select>
              </div>

              {/* Dynamic Locations */}
              {locations?.map((loc, index) => (
                <div key={index} className="border-t pt-4">
                  <div className="mb-2 font-medium text-sm text-gray-700">
                    Entrepot {index + 1}
                  </div>

                  {/* City */}
                  <div className="mb-3">
                    <label
                      htmlFor={`city-${index}`}
                      className="block text-sm font-medium text-gray-900"
                    >
                      Ville
                    </label>
                    <input
                      type="text"
                      id={`city-${index}`}
                      value={loc.city}
                      onChange={(e) =>
                        handleLocationChange(index, "city", e.target.value)
                      }
                      placeholder="Entrez la ville"
                      className="mt-2 block w-full rounded-lg border border-gray-500/30 px-4 py-3 text-gray-900 shadow-sm focus:ring-2 focus:ring-[#b269ae] sm:text-sm"
                      required
                    />
                  </div>

                  {/* Street */}
                  <div>
                    <label
                      htmlFor={`street-${index}`}
                      className="block text-sm font-medium text-gray-700"
                    >
                      Adresse
                    </label>
                    <input
                      type="text"
                      id={`street-${index}`}
                      value={loc.street}
                      onChange={(e) =>
                        handleLocationChange(index, "street", e.target.value)
                      }
                      placeholder="Entrez l'adresse"
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#b269ae] focus:ring-[#b269ae] sm:text-sm h-12 px-3"
                      required
                    />
                  </div>
                </div>
              ))}

              {/* Add More Locations */}
              {/* Add / Remove Location Buttons */}
              <div className="flex gap-6 mt-4">
                <button
                  type="button"
                  onClick={handleAddLocation}
                  className="text-sm text-[#b269ae] font-medium hover:underline"
                >
                  + Ajouter un autre Entrepot
                </button>

                {locations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setLocations(locations.slice(0, -1))}
                    className="text-sm text-red-500 font-medium hover:underline"
                  >
                    Supprimer dernier Entrepot
                  </button>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end gap-6">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 duration-300"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="rounded-md bg-[#b269ae] px-3 py-2 text-sm font-semibold text-white hover:bg-[#9c58a3] duration-300"
              >
                {CreateZoneMutation.isPending ? "Chargement..." : "Ajouter"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddZoneModal;
